/**
 * The tile governor: which tiles a city's citizens work.
 *
 * It maximises a weighted sum of food, shields and gold (`cities.workWeights`) — with
 * one promise kept first: A CITY THAT CAN FEED ITSELF DOES. Pure weighting broke that.
 * Measured in 4-civilisation simulations, cities were choosing three 1-food/3-shield
 * hills over 2-food grassland and starving by one food a turn with fourteen food
 * available against twelve needed — 97 city-turns of needless starvation in two games,
 * shrinking cities that had everything they needed (ADR-0044).
 *
 * So the choice is made in two steps. Take the best-scoring tiles, as before. If they
 * leave the city short of food, trade tiles one at a time — always the swap that costs
 * the least score for each point of food it gains — until the city is fed or no swap
 * can add food. Each swap strictly raises food, so it ends; and it only stops short when
 * the chosen set already holds the most food there is, so a city left starving is one
 * that could not have been fed by any arrangement.
 *
 * Pure: it sees numbers, not the map, which is what makes it testable in a line.
 */

export interface TileOption {
  index: number;
  food: number;
  production: number;
  gold: number;
}

export interface WorkWeights {
  food: number;
  production: number;
  gold: number;
}

export function scoreTile(tile: TileOption, weights: WorkWeights): number {
  return tile.food * weights.food + tile.production * weights.production + tile.gold * weights.gold;
}

/**
 * Pick up to `citizens` tiles from `options`.
 *
 * `foodNeeded` is what the citizens eat less what the city centre grows — the food the
 * worked tiles must supply for the city not to shrink. Returns tile indices, ascending.
 */
export function chooseWorkedTiles(
  options: readonly TileOption[],
  citizens: number,
  foodNeeded: number,
  weights: WorkWeights,
): number[] {
  const ranked = [...options].sort((a, b) => scoreTile(b, weights) - scoreTile(a, weights) || a.index - b.index);
  const chosen = ranked.slice(0, Math.max(0, citizens));
  const spare = ranked.slice(chosen.length);

  let food = chosen.reduce((sum, t) => sum + t.food, 0);

  while (food < foodNeeded) {
    let best: { out: number; in: number; cost: number } | null = null;

    for (let o = 0; o < chosen.length; o++) {
      const out = chosen[o]!;
      for (let i = 0; i < spare.length; i++) {
        const into = spare[i]!;
        const gain = into.food - out.food;
        if (gain <= 0) continue;
        // Score given up per point of food gained. Ties go to the lower tile indices, so
        // the result never depends on the order the options arrived in.
        const cost = (scoreTile(out, weights) - scoreTile(into, weights)) / gain;
        if (
          !best ||
          cost < best.cost ||
          (cost === best.cost && (out.index < chosen[best.out]!.index || (out.index === chosen[best.out]!.index && into.index < spare[best.in]!.index)))
        ) {
          best = { out: o, in: i, cost };
        }
      }
    }

    if (!best) break; // already holding the most food there is

    const leaving = chosen[best.out]!;
    const joining = spare[best.in]!;
    chosen[best.out] = joining;
    spare[best.in] = leaving;
    food += joining.food - leaving.food;
  }

  return chosen.map((t) => t.index).sort((a, b) => a - b);
}
